import { InformationCircle } from 'react-ionicons';
import React, { Fragment, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { usePopper } from 'react-popper';
import clsx from 'clsx';
import { formatBandwidth, formatTerm } from 'screens/SelectServices/utils';
import { formatCurrency } from 'shared/util/formatCurrency';
import { getLabelForIpBlock } from 'screens/OpenQuote/utils';
import { ProductType as AccessProductType } from 'screens/SelectServices/schemas';
import {
  getMobilityMRCAndNRC,
  getMobilityProductSummaryItems,
  MobilityProductType,
} from '../utils';
import { ICartItem } from '../CartProvider';

export interface ProductTooltipProps {
  product: AccessProductType & {
    bandwidth: string;
    access_type: string;
    ip_block: string;
    bundle?: string;
    // For Mobility products
    graniteId?: string;
  };
  inReviewProducts?: boolean;
}

const getStarlinkBundleLabel = (bundle: string) => {
  switch (bundle) {
    case 'enterprise_bundle':
      return 'Enterprise';
    case 'mini_bundle':
      return 'Mini';
    case 'hperf_bundle':
      return 'High Performance';
    default:
      return '';
  }
};

const renderNetworkMonitoring = (product: AccessProductType | undefined) => {
  if (!product?.mns_enhanced && !product?.mns_essential) return null;

  return (
    <li>
      Network monitoring:{' '}
      {product.mns_enhanced
        ? 'MNS Enhanced'
        : product.mns_essential
          ? 'MNS Essentials'
          : ''}
    </li>
  );
};

const getStarlinkServiceTierLabel = (serviceTier: string) => {
  return serviceTier === 'PRIMARY'
    ? 'Primary'
    : serviceTier === 'BACKUP'
      ? 'Backup'
      : undefined;
};

const BroadbandProductDescription = ({
  product,
}: {
  product: AccessProductType & {
    bandwidth: string;
    access_type: string;
    ip_block: string;
  };
}) => (
  <Fragment>
    <li>Service: Broadband</li>
    <li>
      Bandwidth: {formatBandwidth(product?.bandwidth_download ?? '0')}/
      {formatBandwidth(product?.bandwidth_upload ?? '0')}
    </li>
    <li>IP Type: {product?.ip_type}</li>
    {product.ip_block && (
      <li>
        IP Block: {product.ip_block} - {getLabelForIpBlock(product.ip_block)}{' '}
        usable
      </li>
    )}
    <li>Term length: {formatTerm(product.term!)}</li>
    {renderNetworkMonitoring(product)}
    <li>
      Total MRC: {formatCurrency(product?.formal_broadband_pricing?.total_mrc)}
    </li>
  </Fragment>
);

const DIAProductDescription = ({
  product,
}: {
  product: AccessProductType & {
    bandwidth: string;
    access_type: string;
    ip_block: string;
  };
}) => (
  <Fragment>
    <li>Service: DIA</li>
    <li>
      Bandwidth: {formatBandwidth(product?.bandwidth ?? '0')}/
      {formatBandwidth(product?.bandwidth ?? '0')}
    </li>
    <li>Access type: {product?.access_type}</li>
    <li>Building status: {product?.on_net ? 'On-net' : 'Off-net'}</li>
    <li>IP Type: Static</li>
    {product.ip_block && (
      <li>
        IP Block: {product.ip_block} - {getLabelForIpBlock(product.ip_block)}{' '}
        usable
      </li>
    )}
    <li>Term length: {formatTerm(product.term!)}</li>
    {renderNetworkMonitoring(product)}
    <li>Total MRC: {formatCurrency(product?.formal_dia_pricing?.total_mrc)}</li>
  </Fragment>
);

const StarlinkProductDescription = ({
  product,
}: {
  product: AccessProductType & {
    bundle?: string;
  };
}) => (
  <Fragment>
    <li>Service: Starlink</li>
    <li>
      Data plan: {getStarlinkServiceTierLabel(product.service_level_tier!)} (
      {product.data_plan})
    </li>
    <li>Bundle: {getStarlinkBundleLabel(product.bundle!)}</li>
    <li>Term: Month-to-month</li>
    <li>
      Total MRC: {formatCurrency(product?.formal_starlink_pricing?.total_mrc)}
    </li>
  </Fragment>
);

const MobilityProductDescription = ({
  product,
}: {
  product: MobilityProductType;
}) => {
  const MRCAndNRC = getMobilityMRCAndNRC({ device: product });
  const items = getMobilityProductSummaryItems({
    item_details: product,
  } as ICartItem);

  return (
    <Fragment>
      <li>Service: Mobility</li>
      {items.map((item, idx) => (
        <li key={idx}>
          {item.label}: {item.value}
        </li>
      ))}
      {MRCAndNRC.total.nrc > 0 && (
        <li>Total NRC: {formatCurrency(MRCAndNRC.total.nrc)}</li>
      )}
      {MRCAndNRC.total.mrc > 0 && (
        <li>Total MRC: {formatCurrency(MRCAndNRC.total.mrc)}</li>
      )}
    </Fragment>
  );
};

export const AccessProductTooltip = observer(
  ({ product }: ProductTooltipProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const triggerEltRef = useRef<HTMLDivElement>(null);
    const popperEltRef = useRef<HTMLDivElement>(null);

    const { styles, attributes } = usePopper(
      triggerEltRef?.current,
      popperEltRef?.current,
      {
        placement: 'top-start',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [-16, 14],
            },
          },
        ],
      },
    );

    const isBroadband = product.type === 'broadband';
    const isDIA = product.type === 'dia';
    const isStarlink = product.type === 'starlink';
    const isMobility = product.type === 'Mobility';

    const downloadBandwidth =
      product?.bandwidth_download ?? product?.bandwidth ?? '0';

    const uploadBandwidth =
      product?.bandwidth_upload ?? product?.bandwidth ?? '0';

    return (
      <Fragment>
        <div
          ref={triggerEltRef}
          className={clsx(
            'flex items-center justify-center gap-1 rounded-lg bg-green-300 px-3 py-2 text-xs font-bold text-content-flip-default hover:bg-green-200',
            isStarlink &&
              'border border-[#F8FAFC] bg-transparent !text-content-base-default hover:bg-[#F8FAFC] hover:!text-content-flip-default [&>span>svg]:fill-[#F8FAFC] [&>span>svg]:hover:fill-[#191925]',
            isDIA &&
              'border border-[#6C9DFF] bg-transparent !text-content-base-default hover:bg-[#6C9DFF] hover:!text-content-flip-default [&>span>svg]:fill-[#6C9DFF] [&>span>svg]:hover:fill-[#191925]',
            isBroadband &&
              'border !border-green-300 bg-transparent !text-content-base-default hover:!bg-green-300 hover:!text-content-flip-default [&>span>svg]:!fill-[#A7FFB2] [&>span>svg]:hover:!fill-[#191925]',
            isMobility &&
              'border border-purple-300 bg-transparent !text-content-base-default hover:bg-purple-300 hover:!text-content-flip-default [&>span>svg]:fill-purple-300 [&>span>svg]:hover:fill-[#191925]',
          )}
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
        >
          {isMobility ? (
            <span className="flex items-center gap-1">{product.graniteId}</span>
          ) : isStarlink ? (
            <span className="flex items-center gap-1">
              {getStarlinkServiceTierLabel(product.service_level_tier!)}{' '}
              {product.data_plan}
            </span>
          ) : (
            <span>{`${product.provider} ${formatBandwidth(
              downloadBandwidth,
            )} / ${formatBandwidth(uploadBandwidth)}`}</span>
          )}

          <InformationCircle width="16px" height="16px" color="inherit" />
        </div>
        <div ref={popperEltRef} style={styles.popper} {...attributes.popper}>
          {isVisible && (
            <div className="rounded-lg bg-background-base-surface-0 p-5 text-content-base-subdued shadow-elevation3">
              <ul className="list-inside list-disc text-sm text-content-base-subdued">
                {isBroadband ? (
                  <BroadbandProductDescription product={product} />
                ) : isDIA ? (
                  <DIAProductDescription product={product} />
                ) : isStarlink ? (
                  <StarlinkProductDescription product={product} />
                ) : isMobility ? (
                  <MobilityProductDescription product={product} />
                ) : (
                  <li>No product data available</li>
                )}
              </ul>
            </div>
          )}
        </div>
      </Fragment>
    );
  },
);
